/**
 * configureAuth — one-time Amplify setup for a product.
 *
 * Call this at app boot (e.g. in main.tsx) before rendering AuthProvider.
 * Replaces the env-var approach used in individual products.
 *
 * @example
 * ```ts
 * import { configureAuth } from "@d-m-organization/app-shell/auth";
 *
 * configureAuth({
 *   userPoolId: "eu-central-1_XyZ",
 *   userPoolClientId: "abc123",
 *   domain: "auth.dev.gen-pro-hub.com",
 *   redirectSignIn: ["https://qr.dev.gen-pro-hub.com/auth/callback"],
 *   redirectSignOut: ["https://qr.dev.gen-pro-hub.com/"],
 *   scopes: ["openid", "email", "profile", "https://api.gen-pro-hub.com/qr.read"],
 * });
 * ```
 */
import { Amplify } from "aws-amplify";
const DEFAULT_SCOPES = ["openid", "email", "profile"];
let _config = null;
/**
 * Configure Amplify Auth for the current product.
 * Must be called once before any auth operation.
 */
export function configureAuth(config) {
    const domain = config.domain
        .replace(/^https?:\/\//, "")
        .replace(/\/+$/, "");
    _config = { ...config, domain };
    // AL-M3: Cognito surfaces a PreSignUp `LINK_REQUIRED:<id>` in the callback
    // URL's error_description. Amplify processes + strips OAuth params on init
    // (before AuthCallbackPage can read them), so capture it HERE — configureAuth
    // runs at app boot before Amplify.configure — and stash it for the callback.
    try {
        const desc = new URLSearchParams(window.location.search).get("error_description") ?? "";
        const m = /LINK_REQUIRED:([0-9a-fA-F-]{36})/.exec(desc);
        // Set on a link-callback load; otherwise clear so a stale requestId from an
        // abandoned flow can't wrongly redirect a later normal sign-in.
        if (m)
            sessionStorage.setItem("al_pending_link_req", m[1]);
        else
            sessionStorage.removeItem("al_pending_link_req");
    }
    catch {
        /* no window / SSR — ignore */
    }
    Amplify.configure({
        Auth: {
            Cognito: {
                userPoolId: config.userPoolId,
                userPoolClientId: config.userPoolClientId,
                loginWith: {
                    oauth: {
                        domain,
                        scopes: config.scopes ?? DEFAULT_SCOPES,
                        redirectSignIn: config.redirectSignIn,
                        redirectSignOut: config.redirectSignOut,
                        responseType: "code",
                    },
                },
            },
        },
    });
}
/** Retrieve the current auth config. Throws if configureAuth hasn't been called. */
export function getAuthConfig() {
    if (!_config) {
        throw new Error("Auth not configured. Call configureAuth() before using auth features.");
    }
    return _config;
}
